import { Component, Inject, Optional, ViewContainerRef } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { finalize } from 'rxjs/operators';
import { BaseComponent } from '../../../shared/base-components/base.component';
import { DocumentItemKeyType } from '../../enums/document-item-key-type.enum';
import { DocumentItemKeyData } from '../../models/document/document-itemkey.model';
import { DocumentRequirementModel } from '../../models/document/document-requirement.model';
import { DocumentTypeDocumentCategoryTypeMapModel } from '../../models/document/document-type-document-category-type-map.model';
import { RequiredDocumentDetailsModel } from '../../models/document/required-document-details.model';
import { DocumentService } from '../../services/document/document.service';

export class UpsertDocumentRequirementsData {
    constructor(public documentItemKeyCollection, public documentId, public fileName) { }
}

@Component({
    selector: 'upsert-document-requirements-modal',
    templateUrl: './upsert-document-requirements-modal.html'
})
export class UpsertDocumentRequirementsModalComponent extends BaseComponent {
    public form: UntypedFormGroup;
    private allDocumentTypes: any[] = new Array();
    public documentCategoryTypes: any[] = new Array();
    private documentTypeDocumentCategoryTypeMaps: DocumentTypeDocumentCategoryTypeMapModel[] = new Array();
    private requirementIndex = 0;
    public isFormInvalid = true;
    private defaultDocumentRequirementTypeId = 2; // CSRRequired
    private documentItemKeyCollection: DocumentItemKeyData[];
    public documentRequirements: DocumentRequirementModel[] = new Array();
    public isBusy: boolean;
    private fileName: string;
    private documentId = 0;
    public title: string;
    public isBusinessTransaction = false;
    public dropdownTarget: 'body' | HTMLElement;

    // Gets the array of document requirements and casts it to the correct data type
    public get documentRequirementArray() {
        return this.form.get('documentRequirementArray') as UntypedFormArray;
    }

    /**
     * Constructs the modal dialog
     * @param public dialog The dialog reference and dialog data
     * @param fb
     */
    constructor(public dialogRef: MatDialogRef<UpsertDocumentRequirementsModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: UpsertDocumentRequirementsData,
        public containerRef: ViewContainerRef,
        private documentService: DocumentService,
        private fb: UntypedFormBuilder) {

        super();
    }

    public ngOnInit() {
        this.initPageData();
    }

    /**
     * Initializes page data
     */
    private initPageData() {
        this.form = this.fb.group({
            documentRequirementArray: this.fb.array([]),
        });

        // get a reference to the modal container to append the dropdowns to
        this.dropdownTarget = this.containerRef.element.nativeElement as HTMLElement;

        if (this.data.documentId != null) {
            this.documentId = this.data.documentId;
            this.fileName = this.data.fileName;
            this.title = 'Edit Requirements for ' + this.fileName;
        } else {
            this.title = 'Add Requirements';
        }

        this.displayResults = false;

        this.getDocumentTypeDocumentCategoryTypeMaps();
        this.getDocumentLookups();
        if (this.data && this.data.documentItemKeyCollection && this.data.documentItemKeyCollection.length) {
            for (var i = 0; i < this.data.documentItemKeyCollection.length; i++) {
                if (this.data.documentItemKeyCollection[i].documentItemKeyTypeId == DocumentItemKeyType.BusinessTransactionNumber) {
                    this.isBusinessTransaction = true;
                    break;
                }
            }
        }
        this.addDocumentRequirement();
    }

    private getDocumentLookups() {
        this.documentService.getDocumentLookups()
            .subscribe({
                next: res => {
                    this.setDocumentLookupDropDowns(res.result);
                },
                error: err => {
                    this.handleValidationResult(err);
                }
            });
    }

    private getDocumentTypeDocumentCategoryTypeMaps() {
        this.documentService.getDocumentTypeDocumentCategoryTypeMaps()
            .subscribe({
                next: res => {
                    this.documentTypeDocumentCategoryTypeMaps = res.result;
                    this.displayResults = true;
                },
                error: err => {
                    this.handleValidationResult(err);
                }
            });
    }

    private setDocumentLookupDropDowns(documentLookups: any) {

        for (var i = 0; i < documentLookups.documentTypes.length; i++) {
            this.allDocumentTypes.push({ label: documentLookups.documentTypes[i].text, value: documentLookups.documentTypes[i].value });
        }

        for (var i = 0; i < documentLookups.documentCategoryTypes.length; i++) {
            this.documentCategoryTypes.push({ label: documentLookups.documentCategoryTypes[i].text, value: documentLookups.documentCategoryTypes[i].value });
        }

        this.documentItemKeyCollection = this.data.documentItemKeyCollection;
        this.displayResults = true;
    }

    public documentCategoryTypeSelected(requirementViewId, documentCategoryTypeId) {
        if (requirementViewId != null && documentCategoryTypeId != null) {
            const documentRequirement = this.documentRequirements.find(dr => dr.requirementViewId === requirementViewId);
            documentRequirement.documentCategoryTypeId = documentCategoryTypeId;
            documentRequirement.documentTypeId = 0;
            documentRequirement.documentTypes = this.setRequirementDocumentTypes(documentCategoryTypeId);
            this.validateForm();
        }
    }

    private setRequirementDocumentTypes(documentCategoryTypeId: number) {
        const documentCategoryDocumentTypeMaps = this.documentTypeDocumentCategoryTypeMaps.filter(m => m.documentCategoryTypeId == documentCategoryTypeId);
        const result = [];
        for (let i = 0; i < documentCategoryDocumentTypeMaps.length; i++) {
            const documentType = this.allDocumentTypes.find(f => f.value == documentCategoryDocumentTypeMaps[i].documentTypeId);
            if (documentType) {
                result.push(documentType);
            }
        }

        return result;
    }

    public documentTypeSelected(requirementViewId, documentTypeId) {

        if (requirementViewId != null && documentTypeId != null && documentTypeId > 0) {
            const documentRequirement = this.documentRequirements.find(dd => dd.requirementViewId === requirementViewId);
            documentRequirement.documentTypeId = documentTypeId;
            this.validateForm();
        }
    }

    public removeDocumentRequirement(index) {
        this.documentRequirements = this.documentRequirements.slice(0, index).concat(this.documentRequirements.slice(index + 1));
        this.documentRequirementArray.removeAt(index);
    }

    public addDocumentRequirement() {
        const requirementViewId = this.getRequirementIndex();
        const documentRequirement = new DocumentRequirementModel();
        documentRequirement.requirementViewId = requirementViewId;
        documentRequirement.documentRequirementTypeId = this.defaultDocumentRequirementTypeId;
        documentRequirement.documentId = this.documentId;
        documentRequirement.scanLater = false;
        this.documentRequirements.push(documentRequirement);
        this.buildDocumentRequirementValidation(documentRequirement, requirementViewId);
        this.validateForm();
    }

    private buildDocumentRequirementValidation(documentRequirement: DocumentRequirementModel, requirementViewId: string) {
        const requirementGroup = this.fb.group({
            documentCategoryTypeId: [documentRequirement.documentCategoryTypeId, Validators.required],
            documentTypeId: [documentRequirement.documentTypeId, Validators.required],
            scanLater: [documentRequirement.scanLater]
        });

        this.documentRequirementArray.push(requirementGroup);
    }

    /**
     * andles change of the scan later checkbox on a document requirement line.
     * @param scanLater -
     * @param documentRequirement
     */
    public onScanLaterChanged(scanLaterEvent: any, documentRequirementModel: DocumentRequirementModel): void {
        const documentRequirement = this.documentRequirements.find(r => r.requirementViewId === documentRequirementModel.requirementViewId);
        documentRequirement.scanLater = scanLaterEvent.target.checked;
    }

    /**
     * Set the passed in documentItemKeys by first checking the documentItemKeyCollection and then the
     * searchDocumentDataCollection
     */
    private setDocumentItemKeys(documentItemKeys: DocumentItemKeyData[]) {

        if (this.documentItemKeyCollection && this.documentItemKeyCollection.length > 0) {
            documentItemKeys = new Array<DocumentItemKeyData>();
            for (let i = 0; i < this.documentItemKeyCollection.length; i++) {
                const documentItemKeyData = new DocumentItemKeyData();
                documentItemKeyData.documentItemKeyTypeId = this.documentItemKeyCollection[i].documentItemKeyTypeId;
                documentItemKeyData.itemKeyId = this.documentItemKeyCollection[i].itemKeyId;
                documentItemKeys.push(documentItemKeyData);
            }
        }

        return documentItemKeys;
    }

    public save() {
        const validDocumentTypeIds = this.documentRequirements.every(req => req.documentTypeId !== 0);

        if (this.documentRequirements.length == 0 || !validDocumentTypeIds) {
            return;
        }

        this.isBusy = true;
        const request = {
            documentItemKeys: this.documentItemKeyCollection,
            documentRequirements: this.documentRequirements
        };

        this.documentService.addDocumentRequirements(request).pipe(
            finalize(() => {
                this.isBusy = false;
            }))
            .subscribe({
                next: _ => {
                    this.removeAllDocumentRequirements();
                    this.documentRequirementArray.clear();
                    this.dialogRef.close(true);
                },
                error: err => {
                    this.handleValidationResult(err);
                }
            });
    }

    private removeAllDocumentRequirements() {

        if (this.documentRequirements != undefined && this.documentRequirements.length > 0) {
            for (let i = 0; i < this.documentRequirements.length; i++) {
                this.removeDocumentRequirement(this.documentRequirements[i].requirementViewId);
            }
        }
    }

    private validateForm() {
        this.isFormInvalid = false;

        /// TODO: Try to use form controls to do validation to enable/disable button and replace logic below
        for (let j = 0; j < this.documentRequirements.length; j++) {

            if (this.documentRequirements[j].documentCategoryTypeId == undefined ||
                this.documentRequirements[j].documentTypeId == undefined ||
                this.documentRequirements[j].documentCategoryTypeId == 0 ||
                this.documentRequirements[j].documentTypeId == 0) {
                this.isFormInvalid = true;
            }
        }
    }

    /**
     * Remove the document detail from the allDocumentDetails for further validation and update the requiredDocumentDetails instance's requirementSatisfied field
     *
     */
    private validateRequiredDocumentDetails(requiredDocumentDetails: RequiredDocumentDetailsModel, documentDetail: DocumentRequirementModel, allDocumentDetails: DocumentRequirementModel[]) {

        if (documentDetail != null) {
            const index = allDocumentDetails.findIndex(dd => dd.documentTypeId == documentDetail.documentTypeId);
            allDocumentDetails.splice(index, 1);
            requiredDocumentDetails.requirementSatisfied = true;
        } else {
            requiredDocumentDetails.requirementSatisfied = false;
        }
    }

    private getRequirementIndex() {
        return (this.requirementIndex += 1).toString();
    }

    /**
     * Closes the modal dialog with no return result
     */
    public close() {
        this.dialogRef.close();
    }
}
